import { client } from './api'
import { currentUser, ensureMe } from './auth'
import { clearControlCenterToken } from '@/composables/useControlCenterToken'

// Reader-portal routes, verified directly against portal.py (Blueprint
// mounted at /portal in app.py). Shares the same `client`/cookie session as
// api.js and researchApi.js - the portal is just another area of the same
// backend, not a separate service.

export async function portalLogin(email, password) {
  const resp = await client.post('/portal/auth/login', { email, password })
  // Siehe auth.js login() - gleicher Grund (Variante-A-Auftrag).
  clearControlCenterToken()
  const basicUser = { ...resp.data.user, page_keys: [], is_external: true }
  currentUser.value = basicUser
  // ensureMe() never throws and resolves to null on ANY failure, including
  // a transient blip on this immediate follow-up call - that must not undo
  // a login that just genuinely succeeded (see auth.js's login() for the
  // same pattern/reasoning).
  const full = await ensureMe({ force: true })
  if (!full) currentUser.value = basicUser
  return currentUser.value
}

// Response shape varies by case (see portal.py complete_signup):
//   - genuinely new account: {ok, message, user: {...}} + session cookies set
//   - email already had an account: {ok, message} only, NO session - this is
//     deliberate account-enumeration protection, not a bug. Callers must
//     check for `user` before assuming the visitor is now logged in.
export async function completeSignup({ token, password, language }) {
  const resp = await client.post('/portal/auth/complete-signup', { token, password, language })
  if (resp.data.user) {
    const basicUser = { ...resp.data.user, page_keys: [], is_external: true }
    currentUser.value = basicUser
    const full = await ensureMe({ force: true })
    if (!full) currentUser.value = basicUser
  }
  return resp.data
}

// Always resolves with the same generic {ok, message} regardless of whether
// the email is registered - never branch UI behavior on this beyond showing
// the message, or the anti-enumeration protection is undone client-side.
export async function requestPasswordReset(email) {
  const resp = await client.post('/portal/auth/request-reset', { email })
  return resp.data
}

export async function resetPassword({ token, password }) {
  const resp = await client.post('/portal/auth/reset', { token, password })
  return resp.data
}

// Self-registration (portal.py:portal_register). Always resolves with the
// same generic {status, message} regardless of outcome (existing account,
// rate limit, bot heuristic, real success) - never branch UI behavior on
// this beyond showing the message, same anti-enumeration contract as
// requestPasswordReset() above. Only a genuine input error (bad email
// syntax, weak password, missing privacy consent) throws with a specific
// `code` the caller can react to.
export async function registerReader({
  email, password, language, newsletter_opt_in, privacy_accepted, website, form_rendered_at,
}) {
  const resp = await client.post('/portal/auth/register', {
    email, password, language, newsletter_opt_in, privacy_accepted, website, form_rendered_at,
  })
  return resp.data
}

// GET /portal/auth/verify does NOT return JSON - it always issues an HTTP
// redirect to /login?verified=1 or /login?verify_error=1 (see portal.py:
// portal_verify, which deliberately never renders raw JSON since the caller
// is a person clicking a mail link, not an XHR client). A same-origin XHR
// would either be blocked by CORS on the redirect target or silently
// swallow the redirect - there is no way to read success/failure from script
// here. So this performs a real top-level navigation instead of an axios
// call, letting the browser follow the redirect exactly as it would for any
// other link; VerifyView.vue only needs to trigger this and briefly show a
// loading state before the browser leaves the SPA.
export function verifyEmail(token) {
  window.location.href = `${client.defaults.baseURL}/portal/auth/verify?t=${encodeURIComponent(token)}`
}

export async function resendVerification(email) {
  const resp = await client.post('/portal/auth/resend-verify', { email })
  return resp.data
}

export async function updateMe(payload) {
  const resp = await client.patch('/portal/me', payload)
  return resp.data
}

// 'portal.reports' was replaced by the two specific keys below (see
// migrations/0007_portal_views.sql) - both list/get endpoints share the
// same backend function (reports.list_published_reports/get_published_report)
// with a report_type parameter, mirrored here as one function taking the
// type instead of two near-identical copies (see ReportList.vue).
export async function listReports(reportType, { page = 1, limit = 20 } = {}) {
  const path = reportType === 'market_report' ? '/portal/market-reports' : '/portal/equity-research'
  const resp = await client.get(path, { params: { page, limit } })
  return resp.data
}

export async function getReport(reportType, id) {
  const path = reportType === 'market_report' ? '/portal/market-reports' : '/portal/equity-research'
  const resp = await client.get(`${path}/${id}`)
  return resp.data
}

// Dedicated functions for the Equity Research table (EquityResearchView.vue) -
// NOT routed through listReports()/getReport() above, because this endpoint's
// response shape is genuinely different (logo_url, elevator_pitch, case_status,
// has_file - see portal.py:_equity_research_reader_view()), not the plain
// title_de/en+summary_de/en shape the generic Market-Reports list still uses.
export async function listEquityResearch({ page = 1, limit = 20 } = {}) {
  const resp = await client.get('/portal/equity-research', { params: { page, limit } })
  return resp.data
}

export async function getEquityResearch(id) {
  const resp = await client.get(`/portal/equity-research/${id}`)
  return resp.data
}

// Blob download works the same way regardless of which auth mechanism is
// active (cookie or the legacy X-Session-Token header) - the client only
// ever handles a report ID, never a file path (the backend never puts one in
// a response, see reports.py). Path unchanged by the report_type split -
// the backend resolves the type itself from the report row (see portal.py
// portal_report_download()).
export async function downloadReport(id) {
  const resp = await client.get(`/portal/reports/${id}/download`, { responseType: 'blob' })
  return resp.data
}

export async function fetchDashboard() {
  const resp = await client.get('/portal/dashboard')
  return resp.data
}

export async function fetchEquityWatchlist() {
  const resp = await client.get('/portal/equity-watchlist')
  return resp.data
}

export async function fetchMacroNotes() {
  const resp = await client.get('/portal/macro')
  return resp.data
}
